import { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { AtlassianLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { LayoutVersion } from "./product-overlay-template";

export const Modal = ({
    children,
    showBlueHeaderOnMobile,
    showBrandFooter,
    showProductLogoOnLargeScreens,
    size,
    version,
    productLogo
}: PropsWithChildren<{
    showBlueHeaderOnMobile?: boolean;
    showProductLogoOnLargeScreens?: boolean;
    size?: "sm" | "lg";
    showBrandFooter?: boolean;
    version?: LayoutVersion;
    productLogo?: ReactNode;
}>) => {
    const { formatMessage } = useIntl();
    const logoLabel = formatMessage({
        defaultMessage: "Atlassian logo",
        id: "signup.layouts.modal.atlassianLogoLabel"
    });
    return (
        <div
            css={[
                positioningCss,
                showBlueHeaderOnMobile && blueHeaderOnMobileCss,
                version === LayoutVersion.V3 && LayoutV3.positioningCss
            ]}
            data-testid="modal"
        >
            {showProductLogoOnLargeScreens && (
                <div css={logoWrapper}>{productLogo}</div>
            )}
            <div
                css={[
                    modalCss,
                    size === "sm" && smModalCss,
                    size === "lg" && lgModalCss,
                    version === LayoutVersion.V2 && LayoutV2.modalCss,
                    version === LayoutVersion.V3 && LayoutV3.modalCss
                ]}
            >
                {children}
            </div>
            {showBrandFooter && (
                <>
                    <div css={smVisibleCss} aria-hidden="true">
                        <AtlassianLogo
                            appearance="brand"
                            size="small"
                            testId="atlassian-logo-sm-screen"
                            label={logoLabel}
                        />
                    </div>
                    <div css={lgVisibleCss} aria-hidden="true">
                        <AtlassianLogo
                            appearance="inverse"
                            size="small"
                            testId="atlassian-logo-lg-screen"
                            label={logoLabel}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

const blueHeaderOnMobileCss = css({
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1440 518' preserveAspectRatio='none' fill='%23E9F2FE' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1331.5 -20.8369L-38.614 -36.9811L-43.579 433.588H243.576L248.105 518L1598.49 435.131L1574.37 -14.3789L1331.5 0.525597V-20.8369Z'/%3E%3C/svg%3E%0A")`,
    backgroundSize: "100% 120px",
    backgroundPosition: "center 0",
    backgroundRepeat: "no-repeat",
    paddingBlockStart: 42,
    [media.above.sm]: {
        backgroundImage: "none"
    }
});

const smVisibleCss = css({
    display: "block",
    [media.above.sm]: {
        display: "none !important"
    }
});

const lgVisibleCss = css({
    display: "none",
    [media.above.sm]: {
        display: "block !important"
    }
});

const modalCss = css({
    overflow: "hidden",
    transition: "width 0.5s ease-in-out",
    maxWidth: "100%",
    [media.above.sm]: {
        backgroundColor: token("elevation.surface"),
        borderRadius: 8,
        boxShadow: token("elevation.shadow.overlay"),
        minHeight: 400
    }
});

const LayoutV2 = {
    modalCss: css({
        [media.above.sm]: {
            borderRadius: 3
        }
    })
};

const LayoutV3 = {
    modalCss: css({
        [media.above.sm]: {
            borderRadius: 12 // todo replace with radius.xlarge token when available
        }
    }),
    positioningCss: css({
        [media.above.sm]: {
            justifyContent: "flex-start",
            padding: token("space.1000")
        }
    })
};

const smModalCss = css({
    width: 560
});

const lgModalCss = css({
    width: 1140
});

const positioningCss = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 32,
    padding: 24,
    [media.above.sm]: {
        justifyContent: "center",
        minHeight: "100vh",
        padding: 32,
        ["@supports (min-height: 100dvh)"]: {
            minHeight: "100dvh"
        }
    }
});

const logoWrapper = css({
    [media.above.sm]: {
        color: "white"
    }
});
